"use client";

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { getOffers } from "@/app/actions/offer";
import { useSession } from "next-auth/react";

const OffersContext = createContext();

export function OffersProvider({ children }) {
  const [offers, setOffers] = useState([]);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { data: session } = useSession();

  // Fetch offers data
  const fetchOffers = useCallback(async () => {
    if (!session?.user?.id) {
      setIsLoading(false);
      return;
    }
    
    try {
      setIsLoading(true);
      const offersData = await getOffers(session.user.id);
      setOffers(offersData);
      
      // Auto-select first offer if none selected and offers exist
      if (offersData.length > 0 && !selectedOffer) {
        setSelectedOffer(offersData[0]);
      }
      
      setError(null);
    } catch (err) {
      console.error("Error fetching offers:", err);
      setError(err);
      setOffers([]);
    } finally {
      setIsLoading(false);
    }
  }, [session?.user?.id, selectedOffer]);

  // Initial fetch when session is available
  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  // Add new offer to state
  const addOffer = useCallback((newOffer) => {
    setOffers(prev => [...prev, newOffer]);
    // Auto-select new offer if it's the first one
    if (offers.length === 0) {
      setSelectedOffer(newOffer);
    }
  }, [offers.length]);

  // Update existing offer in state
  const updateOffer = useCallback((updatedOffer) => {
    setOffers(prev => prev.map(offer => 
      offer.id === updatedOffer.id ? updatedOffer : offer
    ));
    // Update selected offer if it's the one being updated
    if (selectedOffer?.id === updatedOffer.id) {
      setSelectedOffer(updatedOffer);
    }
  }, [selectedOffer]);

  // Remove offer from state
  const removeOffer = useCallback((offerId) => {
    setOffers(prev => prev.filter(offer => offer.id !== offerId));
    // Clear selected offer if it's the one being removed
    if (selectedOffer?.id === offerId) {
      const remainingOffers = offers.filter(offer => offer.id !== offerId);
      setSelectedOffer(remainingOffers.length > 0 ? remainingOffers[0] : null);
    }
  }, [selectedOffer, offers]);

  // Manual refresh function
  const refreshOffers = useCallback(() => {
    return fetchOffers();
  }, [fetchOffers]);

  const value = {
    // State
    offers,
    selectedOffer,
    isLoading,
    error,
    session,
    
    // Actions
    setSelectedOffer,
    addOffer,
    updateOffer,
    removeOffer,
    refreshOffers,
    
    // Computed properties
    hasOffers: offers.length > 0,
    offer: selectedOffer, // Alias for backward compatibility
  };

  return (
    <OffersContext.Provider value={value}>
      {children}
    </OffersContext.Provider>
  );
}

export function useOffers() {
  const context = useContext(OffersContext);
  if (context === undefined) {
    throw new Error('useOffers must be used within an OffersProvider');
  }
  return context;
}